// Valor em reais por extenso, em português.
//
// Arquivo separado, e sem UM import, por uma razão: é a única lógica do front que
// produz texto de DOCUMENTO LEGAL. A cláusula 3.1 do contrato de IRPF diz "os
// honorários de R$ XXX,00 (XXX reais)" — o modelo pede as duas formas, e o extenso
// é justamente o campo que o aluno confere antes de mandar ao cliente. Errar aqui
// é errar num contrato assinado.
//
// Sem dependências, a prova `app/tests/extenso.mjs` transpila este arquivo sozinho
// e roda os casos. Dentro de `contrato.ts` (que importa supabase e fflate) isso
// exigiria bundlar meio app para testar aritmética.

const UNIDADES = [
  "", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove",
  "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete",
  "dezoito", "dezenove",
];
const DEZENAS = [
  "", "", "vinte", "trinta", "quarenta", "cinquenta",
  "sessenta", "setenta", "oitenta", "noventa",
];
const CENTENAS = [
  "", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos",
  "seiscentos", "setecentos", "oitocentos", "novecentos",
];

/**
 * 0–999 em palavras.
 *
 * "cem" é caso próprio: 100 é "cem", mas 101 é "cento e um" — "cento" sozinho não
 * existe. E de 10 a 19 não há dezena + unidade ("dezesseis", não "dez e seis"),
 * daí o ramo do `< 20`.
 */
function trio(n: number): string {
  if (n === 0) return "";
  if (n === 100) return "cem";

  const c = Math.floor(n / 100);
  const resto = n % 100;
  const partes: string[] = [];

  if (c) partes.push(CENTENAS[c]);
  if (resto) {
    if (resto < 20) partes.push(UNIDADES[resto]);
    else {
      const d = Math.floor(resto / 10);
      const u = resto % 10;
      partes.push(u ? `${DEZENAS[d]} e ${UNIDADES[u]}` : DEZENAS[d]);
    }
  }
  return partes.join(" e ");
}

/**
 * Inteiro em palavras, até bilhões.
 *
 * Duas irregularidades do português que não são detalhe:
 *
 *  1. **"mil" não leva "um"**: 1.000 é "mil", não "um mil". Já 1.000.000 é "um
 *     milhão" — a unidade aparece.
 *  2. **A ligação do último grupo muda**: 1.234 é "mil duzentos e trinta e
 *     quatro" (sem "e"), mas 1.002 é "mil e dois" e 1.100 é "mil e cem". Entra "e"
 *     quando o último grupo PRESENTE é menor que cem ou é centena redonda.
 *
 *     "presente" é a parte que custou um caso de teste: a primeira versão olhava
 *     sempre as centenas, e 2.500.000 saía "dois milhões quinhentos mil" em vez de
 *     "dois milhões E quinhentos mil". Com as centenas em zero, quem decide a
 *     ligação é o grupo dos milhares — e por isso a regra tem de olhar o último
 *     grupo que de fato entrou na frase, não uma posição fixa.
 */
export function inteiroEmPalavras(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  if (n === 0) return "zero";

  const bilhoes = Math.floor(n / 1_000_000_000);
  const milhoes = Math.floor((n % 1_000_000_000) / 1_000_000);
  const milhares = Math.floor((n % 1_000_000) / 1000);
  const centenas = n % 1000;

  // Cada parte carrega o VALOR do seu grupo, porque é ele que decide a ligação.
  const partes: Array<{ texto: string; valor: number }> = [];
  if (bilhoes) partes.push({ texto: bilhoes === 1 ? "um bilhão" : `${trio(bilhoes)} bilhões`, valor: bilhoes });
  if (milhoes) partes.push({ texto: milhoes === 1 ? "um milhão" : `${trio(milhoes)} milhões`, valor: milhoes });
  if (milhares) partes.push({ texto: milhares === 1 ? "mil" : `${trio(milhares)} mil`, valor: milhares });
  if (centenas) partes.push({ texto: trio(centenas), valor: centenas });

  if (partes.length <= 1) return partes[0]?.texto ?? "";

  const ultimo = partes.pop() as { texto: string; valor: number };
  const ligaComE = ultimo.valor < 100 || ultimo.valor % 100 === 0;
  return `${partes.map((p) => p.texto).join(", ")}${ligaComE ? " e " : " "}${ultimo.texto}`;
}

/**
 * "R$ 1.234,56 (mil duzentos e trinta e quatro reais e cinquenta e seis centavos)"
 *
 * O arredondamento acontece ANTES de separar reais de centavos, de propósito: com
 * float, `19804.75 * 100` não é exatamente 1980475, e separar primeiro deixaria o
 * número e o extenso discordando em um centavo — num documento em que os dois
 * aparecem lado a lado.
 */
export function reaisPorExtenso(valor: number): string {
  const centavosTotais = Math.round(valor * 100);
  const inteiros = Math.floor(centavosTotais / 100);
  const centavos = centavosTotais % 100;

  const numero = (centavosTotais / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const partes: string[] = [];
  // Zero real com centavos não diz "zero reais e cinquenta centavos" — diz só
  // "cinquenta centavos". Mas R$ 0,00 diz "zero reais", senão o parêntese fica
  // vazio.
  if (inteiros || !centavos) {
    partes.push(`${inteiroEmPalavras(inteiros)} ${inteiros === 1 ? "real" : "reais"}`);
  }
  if (centavos) {
    partes.push(`${inteiroEmPalavras(centavos)} ${centavos === 1 ? "centavo" : "centavos"}`);
  }

  return `${numero} (${partes.join(" e ")})`;
}
